import { Button, Input, MediaCard, Modal } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validators } from '@sendible/common';
import { validateImage } from '@sendible/common/utils/image-utils';
import { MediaPreviewProps } from '@sendible/shared-state-bridge';
import { useGetLinkPreview } from '@sendible/frontendv2/src/models/linkPreview';
import { MediaPreview } from '@sendible/frontendv2/src/components';
import { Container, Footer, FooterContent, GridContent, Header, SelectionCount } from '../styles/index.styles';
import { useComposeBoxContext } from '../../../composeBoxContext';
import { VALIDATION_DEBOUNCE_MS } from '../../../consts';
import { BlankState } from '../BlankState';
import { LoadingState } from '../LoadingState';

type ValidatedMedia = {
  src: string;
  type: MediaPreviewProps['mediaList'][number]['type'];
  alt: string;
};
type PreviewMedia = MediaPreviewProps['mediaList'][number];

export const URLModal = () => {
  const { setIsURLModalOpen, uploadUrlsToCompose, urlModalInitialUrl, getRemainingMediaLimit } = useComposeBoxContext();
  const { t } = useTranslation(['compose_box', 'common_terms']);
  const [url, setUrl] = useState(urlModalInitialUrl || '');
  const [selectedItems, setSelectedItems] = useState<UrlUploadFileType[]>([]);
  const [searchUrl, setSearchUrl] = useState(urlModalInitialUrl || '');
  const [showPreview, setShowPreview] = useState(false);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const { data: linkPreview, isLoading, isError } = useGetLinkPreview(searchUrl);
  const [validMedia, setValidMedia] = useState<ValidatedMedia[]>([]);
  const [isValidatingMedia, setIsValidatingMedia] = useState(false);
  const [urlFeedback, setUrlFeedback] = useState<
    | {
        message?: string;
        type?: 'success' | 'danger' | 'warning' | 'info';
      }
    | undefined
  >(undefined);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentMaxLimit = getRemainingMediaLimit();

  const buildValidatedMedia = useCallback(
    ({ src, type, alt }: { src: string; type?: string; alt?: string }) =>
      validateImage(src).then((isValid) => {
        if (!isValid) {
          return null;
        }

        return { src, type, alt };
      }),
    [validateImage]
  );

  const mediaList = useMemo<PreviewMedia[]>(
    () =>
      validMedia.map(({ src, type, alt }) => ({
        url: src,
        type,
        id: src,
        isGif: false,
        name: alt || src,
        downloadUrl: src,
      })),
    [validMedia]
  );

  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;
    const mediaItems = linkPreview?.media ?? [];

    const validateMedia = async () => {
      if (!mediaItems.length) {
        await Promise.resolve();

        if (isCancelled) {
          return;
        }

        setValidMedia([]);
        setSelectedItems((prev) => (prev.length ? [] : prev));
        setIsValidatingMedia(false);

        return;
      }

      setIsValidatingMedia(true);

      const results = await Promise.all(mediaItems.map(buildValidatedMedia));

      if (isCancelled) {
        return;
      }

      const filtered = results.filter((media): media is ValidatedMedia => Boolean(media));

      setValidMedia(filtered);
      setSelectedItems((prev) => prev.filter((item) => filtered.some((media) => media.src === item.url)));
      setIsValidatingMedia(false);
    };

    validateMedia();

    return () => {
      isCancelled = true;
    };
  }, [buildValidatedMedia, linkPreview?.media]);

  const handleMediaPreview = (id: string) => {
    const mediaIndex = mediaList.findIndex((media) => media.id === id);

    if (mediaIndex !== -1) {
      setPreviewInitialItem(mediaIndex);
      setShowPreview(true);
    }
  };
  const validateUrl = useCallback(
    (inputUrl: string) => {
      if (inputUrl.trim() === '') {
        setUrlFeedback(undefined);

        return;
      }

      if (!validators.validateUrl(inputUrl)) {
        setUrlFeedback({
          message: t('validURL') || '',
          type: 'danger',
        });
      } else {
        setUrlFeedback(undefined);
      }
    },
    [t]
  );

  const handleUrlChange = useCallback(
    (value: string) => {
      setUrl(value);

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      validationTimeoutRef.current = setTimeout(() => {
        validateUrl(value);
      }, VALIDATION_DEBOUNCE_MS);
    },
    [validateUrl]
  );

  const handleKeyPress = useCallback((key: string, text: string) => {
    if (key === 'Enter') {
      if (text.trim() !== '' && !validators.validateUrl(text)) {
        setUrlFeedback({
          message: 'Please enter a valid URL.',
          type: 'danger',
        });
      } else {
        setUrlFeedback(undefined);
        setSelectedItems([]);
        setSearchUrl(text);
      }
    }
  }, []);
  const handleMediaSelection = useCallback((media: UrlUploadFileType) => {
    setSelectedItems((prev) => {
      const exists = prev.some((selectedItem) => selectedItem.url === media.url);

      if (exists) {
        return prev.filter((selectedItem) => selectedItem.url !== media.url);
      }

      if (prev.length >= currentMaxLimit) {
        return prev;
      }

      return [...prev, media];
    });
  }, []);
  const handleSubmit = useCallback((value: string) => {
    if (validators.validateUrl(value)) {
      setSelectedItems([]);
      setSearchUrl(value);
      setUrlFeedback(undefined);
    } else {
      setUrlFeedback({
        message: t('validURL') || '',
        type: 'danger',
      });
    }
  }, []);

  const handleImportClick = useCallback(() => {
    if (selectedItems.length > 0) {
      uploadUrlsToCompose(selectedItems);
      setIsURLModalOpen(false);
    }
  }, [selectedItems, uploadUrlsToCompose, setIsURLModalOpen]);

  const subheaderContent = (
    <Header>
      <Input
        icon="search"
        size="sm"
        label={t('from_url_input_label')}
        change={handleUrlChange}
        keyPress={handleKeyPress}
        feedback={urlFeedback}
        value={url}
        button={{ label: t('search') || '', click: () => handleSubmit(url), disabled: url.trim() === '' }}
      />
    </Header>
  );

  const shouldShowBlankState = (!isLoading && !isValidatingMedia && mediaList.length === 0) || isError;
  const shouldShowLoadingState = isLoading || isValidatingMedia;

  return (
    <>
      <Modal
        portalDomId="layer-root"
        close={() => setIsURLModalOpen(false)}
        headingIcon="globe"
        preventEscapeClose={showPreview}
        headingTitle={t('from_url') as string}
        subHeaderContent={subheaderContent}
        variation="large"
        footerContent={
          searchUrl ? (
            <Footer>
              <FooterContent>
                <SelectionCount>{t('selection_count', { selected: selectedItems.length, limit: currentMaxLimit })}</SelectionCount>
                <Button
                  appearance="primary"
                  disabled={selectedItems.length === 0}
                  label={selectedItems.length === 1 ? t('import_image') || '' : t('import_images', { count: selectedItems.length }) || ''}
                  onClick={handleImportClick}
                  size={14}
                  variation="fill"
                />
              </FooterContent>
            </Footer>
          ) : undefined
        }
      >
        {searchUrl && (
          <Container>
            {shouldShowBlankState ? (
              <BlankState />
            ) : (
              <GridContent>
                {shouldShowLoadingState ? (
                  <LoadingState skeletonCount={8} />
                ) : (
                  validMedia.map(({ src, type, alt }, index) => {
                    const isSelected = selectedItems.some((selectedItem) => selectedItem.url === src);
                    const isDisabled = !isSelected && selectedItems.length >= currentMaxLimit;

                    return (
                      <MediaCard
                        id={src}
                        key={`${src}-${index}`}
                        name={alt || ''}
                        isSelected={isSelected}
                        open={() => handleMediaPreview(src)}
                        select={isDisabled ? undefined : () => handleMediaSelection({ url: src, name: alt })}
                        showCheckboxWithoutHover={isSelected || selectedItems.length > 0}
                        thumbUrl={src}
                        type={type}
                        updateDate=""
                        uploadingText=""
                        customClass={isDisabled ? 'disabled-mediacard' : ''}
                      />
                    );
                  })
                )}
              </GridContent>
            )}
          </Container>
        )}
      </Modal>
      {showPreview && mediaList.length > 0 && (
        <MediaPreview
          initialMediaIndex={previewInitialItem}
          mediaList={mediaList}
          onClose={() => setShowPreview(false)}
          shouldShowActions={false}
        />
      )}
    </>
  );
};
